// /forgot-password — 寄送密碼重設連結（00 §4.6 / core §4.6）。
// POST /v3/auth/forgot-password 一律回 200（防帳號列舉）：不論 email 是否存在，
// UI 都顯示同一則「若帳號存在，重設信已寄出」。

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, forgotPassword } from '../../lib/api';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to="/home" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email.trim());
      // 防列舉：不論帳號是否存在都顯示同一成功訊息。
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? t('auth.forgot.failGeneric', { msg: err.message }) : t('auth.forgot.failNetwork'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
      <section className="card" style={{ width: 'min(400px, 100%)' }}>
        <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-4xl)', letterSpacing: 'var(--letter-spacing-tight)' }}>{t('auth.forgot.title')}</h1>
        {sent ? (
          <>
            <p className="text-secondary">{t('auth.forgot.sentBody', { email: email.trim() })}</p>
            <p style={{ marginBottom: 0 }}>
              <Link to="/login">{t('auth.backToLogin')}</Link>
            </p>
          </>
        ) : (
          <>
            <p className="text-secondary" style={{ marginTop: 'calc(-1 * var(--spacing-4))' }}>
              {t('auth.forgot.subtitle')}
            </p>
            <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--spacing-6)' }}>
              <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
                  {t('auth.email')}
                </span>
                <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={error !== null} />
              </label>
              {error && (
                <p className="error-text" role="alert" style={{ margin: 0 }}>
                  {error}
                </p>
              )}
              <button type="submit" className="btn-primary" disabled={busy || !email.trim()}>
                {busy ? t('auth.forgot.submitBusy') : t('auth.forgot.submit')}
              </button>
            </form>
            <p className="text-secondary" style={{ marginBottom: 0, fontSize: 'var(--font-size-md)' }}>
              {t('auth.forgot.rememberPrompt')}<Link to="/login">{t('auth.backToLogin')}</Link>
            </p>
          </>
        )}
      </section>
    </div>
  );
}
